import React, { createContext, useContext, useEffect, useState } from 'react';
import { cardTemplateApi } from '@/user/api';
import type { GradientColorConfig } from '@/types/eCard';

/**
 * 预览主题配色（来源于「主题配置」页保存的模板配色）
 * 通过 PreviewThemeProvider 在预览根部拉取一次模板配置，
 * 预览内任意子组件用 usePreviewTheme() 取色。
 */
export interface PreviewTheme {
  /** 主题色（纯色，用于强调文字、图标等） */
  themeColor: string;
  /** 主题色 CSS（兼容旧字段，已废弃，新版用 backgroundGradient） */
  themeGradient: string;
  /** 主题背景色（纯色兜底主色） */
  backgroundColor: string;
  /** 主题背景色 CSS（渐变优先，可直接用于 background，支持纯色/线性/径向） */
  backgroundGradient: string;
  /** 按钮颜色（纯色兜底主色） */
  buttonColor: string;
  /** 按钮颜色 CSS（渐变优先） */
  buttonGradient: string;
  /** 标题/强调文字颜色 */
  textHighlightColor: string;
  /** 正文文字颜色 */
  textNormalColor: string;
  /** 导航栏背景色 */
  navBarBgColor: string;
  /** 导航栏文字颜色 */
  navBarTextColor: string;
  /** 底部导航选中色 */
  tabHighlightColor: string;
}

const FALLBACK_COLOR = '#1DA9FF';

const isValidHex = (hex: string) => /^#[0-9a-fA-F]{6}$/.test(String(hex || '').trim());

/** 将渐变配置转换为可直接使用的 CSS background 值（与主题配置页逻辑一致） */
export function gradientCss(g?: GradientColorConfig | null): string {
  const colors = (g?.colors || []).filter((c) => isValidHex(String(c || '').trim()));
  const list = colors.length ? colors : [FALLBACK_COLOR];
  if (!g || g.mode === 'solid' || list.length < 2) return list[0];
  if (g.mode === 'radial') return `radial-gradient(circle at 50% 50%, ${list.join(', ')})`;
  const angle = Number.isFinite(g.angle) ? Math.round(g.angle) : 135;
  return `linear-gradient(${angle}deg, ${list.join(', ')})`;
}

/** 十六进制颜色追加透明度（如 withAlpha('#1DA9FF', '1A')），非法颜色原样返回 */
export function withAlpha(hex: string, alpha: string): string {
  const h = String(hex || '').trim();
  return isValidHex(h) ? `${h}${alpha}` : h;
}

const defaultTheme: PreviewTheme = {
  themeColor: FALLBACK_COLOR,
  themeGradient: FALLBACK_COLOR,
  backgroundColor: FALLBACK_COLOR,
  backgroundGradient: FALLBACK_COLOR,
  buttonColor: FALLBACK_COLOR,
  buttonGradient: FALLBACK_COLOR,
  textHighlightColor: '#000000',
  textNormalColor: '#666666',
  navBarBgColor: '#ffffff',
  navBarTextColor: '#333333',
  tabHighlightColor: FALLBACK_COLOR,
};

const PreviewThemeContext = createContext<PreviewTheme>(defaultTheme);

/** 预览内获取当前主题配色 */
// eslint-disable-next-line react-refresh/only-export-components
export const usePreviewTheme = () => useContext(PreviewThemeContext);

export default function PreviewThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<PreviewTheme>(defaultTheme);

  useEffect(() => {
    let mounted = true;
    cardTemplateApi
      .getTemplate()
      .then((res) => {
        if (!mounted || res.code !== 200 || !res.data) return;
        const c = res.data.colorConfig;
        if (!c) return;
        // 后端以字符串（CSS 值）传递 buttonColor / backgroundGradient；兼容旧对象格式
        const toCss = (v: any): string => (typeof v === 'string' ? v : gradientCss(v));
        const firstHex = (s: string): string => (String(s || '').match(/#[0-9a-fA-F]{6}/g) || [])[0] || '';
        const themeColor = firstHex(c.themeColor) || defaultTheme.themeColor;
        // 主题背景色：兼容旧字段 themeGradient
        const backgroundColor = firstHex(c.backgroundColor) || themeColor;
        const bgCss = toCss(c.backgroundGradient) || toCss(c.themeGradient);
        const buttonCss = toCss(c.buttonColor) || toCss(c.buttonGradient);
        const buttonColor = firstHex(buttonCss) || defaultTheme.buttonColor;
        setTheme({
          themeColor,
          // 旧 themeGradient 已废弃，仅兼容旧数据使用
          themeGradient: themeColor,
          backgroundColor,
          // 后端未返回时，回退为对应纯色，保证预览配色与「主题配置」保存的值一致
          backgroundGradient: bgCss || backgroundColor,
          buttonColor,
          buttonGradient: buttonCss || buttonColor,
          textHighlightColor: isValidHex(c.textHighlightColor) ? c.textHighlightColor : defaultTheme.textHighlightColor,
          textNormalColor: isValidHex(c.textNormalColor) ? c.textNormalColor : defaultTheme.textNormalColor,
          navBarBgColor: isValidHex(res.data.navBarConfig?.backgroundColor)
            ? res.data.navBarConfig.backgroundColor
            : defaultTheme.navBarBgColor,
          navBarTextColor: isValidHex(res.data.navBarConfig?.textColor)
            ? res.data.navBarConfig.textColor
            : defaultTheme.navBarTextColor,
          tabHighlightColor: isValidHex(res.data.tabbarConfig?.highlightColor)
            ? res.data.tabbarConfig.highlightColor
            : defaultTheme.tabHighlightColor,
        });
      })
      .catch(() => {
        // 拉取失败时保持默认配色
      });
    return () => {
      mounted = false;
    };
  }, []);

  return <PreviewThemeContext.Provider value={theme}>{children}</PreviewThemeContext.Provider>;
}
